import { Box, Collapse, Group, Stack, Text } from "@mantine/core"
import { CertificateRequestStatus } from "@tfg/db/browser"
import { Badge, BadgeVariant, ClickableText, ToggleCollapse } from "@tfg/ui"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CertificateByOrganizationDto,
  FindCertificateRequestsByCertificateIdItem,
  FindCertificateRequestsByCertificateIdItemGroup,
} from "~/gen/api"
import { CertificateRequestActions } from "~/pages/Brand/Certificates/components/CertificateRequestActions"
import { CertificateRequestValidation } from "~/pages/Brand/Certificates/components/CertificateRequestValidation"

type SubmittedCertificateRequestsProps = {
  data: FindCertificateRequestsByCertificateIdItemGroup
  certificate: CertificateByOrganizationDto
  openedStatus: CertificateRequestStatus | null
  setOpenedStatus: React.Dispatch<
    React.SetStateAction<CertificateRequestStatus | null>
  >
}

type BatchSelectAction = {
  text: string
  onClick: () => void
}

export const SubmittedCertificateRequests: React.FC<
  SubmittedCertificateRequestsProps
> = ({
  data,
  certificate,
  openedStatus,
  setOpenedStatus,
}: SubmittedCertificateRequestsProps) => {
  const { t } = useTranslation(["certificates", "common"])

  const [checkedCertificateRequests, setCheckedCertificateRequests] = useState<
    string[]
  >([])

  const opened = openedStatus === CertificateRequestStatus.SUBMITTED
  const toggle = () => {
    setOpenedStatus(opened ? null : CertificateRequestStatus.SUBMITTED)
  }

  const seeMoreLabelComponent = (
    <Group gap="xs">
      <Text fw={600} size="md" style={{ fontWeight: 600 }}>
        {t("certificates:certificateRequestStatus.SUBMITTED")}
      </Text>
      <Badge
        variant={BadgeVariant.SECONDARY}
        text={data.count.toString()}
        size="xs"
      />
    </Group>
  )

  const allChecked = useMemo(
    () => checkedCertificateRequests.length === data.count,
    [checkedCertificateRequests.length, data.count]
  )

  const handleBatchSelectAction = useCallback((): BatchSelectAction => {
    if (allChecked) {
      return {
        text: t("common:actionButtons.deselectAll"),
        onClick: () => {
          setCheckedCertificateRequests([])
        },
      }
    } else {
      return {
        text: t("common:actionButtons.selectAll"),
        onClick: () => {
          setCheckedCertificateRequests(
            data.certificateRequests.map((request) => request.id)
          )
        },
      }
    }
  }, [allChecked, data.certificateRequests, t])

  if (!data.certificateRequests || data.count === 0) {
    return null
  }

  return (
    <Stack gap={0} mb="var(--mantine-spacing-xs)">
      <Group justify="space-between">
        <ToggleCollapse
          open={opened}
          toggle={toggle}
          seeMoreLabel={seeMoreLabelComponent}
          seeLessLabel={seeMoreLabelComponent}
        />
        {opened ? <ClickableText {...handleBatchSelectAction()} /> : null}
      </Group>
      <Collapse in={opened}>
        <Box mah="calc(100vh - 20rem)" style={{ overflowY: "auto" }}>
          <Stack>
            {data.certificateRequests.map(
              (
                certificateRequest: FindCertificateRequestsByCertificateIdItem
              ) => (
                <CertificateRequestValidation
                  key={certificateRequest.id}
                  certificateRequest={certificateRequest}
                  certificate={certificate}
                  checked={checkedCertificateRequests.includes(
                    certificateRequest.id
                  )}
                  onCheckboxChange={(checked) => {
                    setCheckedCertificateRequests((prev) =>
                      checked
                        ? Array.from(new Set([...prev, certificateRequest.id]))
                        : Array.from(
                            new Set(
                              prev.filter((id) => id !== certificateRequest.id)
                            )
                          )
                    )
                  }}
                />
              )
            )}
          </Stack>
        </Box>
        <Stack
          pos="sticky"
          bottom={0}
          mah="9vh"
          p="sm"
          mt="var(--mantine-spacing-xs)"
        >
          <CertificateRequestActions
            certificateRequestIds={checkedCertificateRequests}
            certificate={certificate}
            onActionSelected={() => {
              setCheckedCertificateRequests([])
            }}
          />
        </Stack>
      </Collapse>
    </Stack>
  )
}
